import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const ss = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── S1: Top-Down Selection Flow ──
function TopDownFlow() {
  const [active, setActive] = useState(null);
  const steps = [
    {
      num: "01", icon: "🌍", title: "Economic Analysis", color: blue2,
      question: "What is the current economic climate?",
      actions: ["Is the economy in recession or expansion?", "Are interest rates rising or falling?", "Which sectors thrive in this environment?"],
      example: "In an expanding economy with falling rates → equity funds and long-duration bond funds tend to outperform.",
    },
    {
      num: "02", icon: "📦", title: "Category Selection", color: gold,
      question: "Which asset class fits the client's KYC profile?",
      actions: ["Equity: growth-focused, long time horizon", "Fixed Income: income and capital preservation", "Balanced: mix of both for moderate profiles"],
      example: "Client is 35, high risk tolerance, 20-year horizon → equity-heavy allocation.",
    },
    {
      num: "03", icon: "🔍", title: "Fund Selection", color: green,
      question: "Which specific fund within that category is best?",
      actions: ["Apply the 5 P's framework (People, Process, Performance, Portfolio, Price)", "Compare risk-adjusted returns over 3, 5, 10 years", "Check MER — the single most reliable predictor of future performance"],
      example: "Two Canadian equity funds: same category, same benchmark. The one with lower MER and stable manager tenure wins.",
    },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gap: 8 }}>
        {steps.map((step, i) => (
          <div key={i} onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? step.color + "55" : border}`, borderRadius: 8, padding: "18px 20px", cursor: "pointer", transition: "all 0.2s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: `${step.color}18`, border: `1px solid ${step.color}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", flexShrink: 0 }}>{step.icon}</div>
                <div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.65rem", color: step.color, letterSpacing: "0.12em", textTransform: "uppercase" }}>Step {step.num}</div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 700, color: white }}>{step.title}</div>
                  <div style={{ fontSize: "0.78rem", color: muted, marginTop: 2 }}>{step.question}</div>
                </div>
              </div>
              <span style={{ color: active === i ? step.color : muted, fontSize: "1rem", display: "inline-block", transform: active === i ? "rotate(90deg)" : "none", transition: "transform 0.25s", flexShrink: 0 }}>›</span>
            </div>
            {active === i && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${border}` }}>
                <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
                  {step.actions.map((a, j) => (
                    <div key={j} style={{ display: "flex", gap: 8, fontSize: "0.84rem", color: textCol }}>
                      <span style={{ color: step.color, flexShrink: 0 }}>→</span>{a}
                    </div>
                  ))}
                </div>
                <div style={{ background: navy3, borderLeft: `3px solid ${step.color}`, borderRadius: 6, padding: "10px 14px", fontSize: "0.8rem", color: muted, lineHeight: 1.7 }}>
                  💡 {step.example}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── S2: The 5 P's ──
function FivePsExplorer() {
  const [active, setActive] = useState(0);
  const ps = [
    {
      letter: "P", name: "People", icon: "👤", color: blue2,
      definition: "Who is the portfolio manager and how long have they been running this fund?",
      whyMatters: "A fund's track record belongs to its manager, not the fund itself. If the manager who built that 10-year record left 6 months ago, past performance is irrelevant to the future.",
      lookFor: ["Manager tenure — ideally 5+ years in the same role", "Manager's credentials (CFA, experience)", "Depth of the team behind the manager", "What happens if the manager leaves? (Key-person risk)"],
      redFlag: "High turnover of portfolio managers is a major red flag — it signals instability and makes the fund's history unreliable.",
    },
    {
      letter: "P", name: "Process", icon: "⚙️", color: gold,
      definition: "What is the fund's investment philosophy and methodology?",
      whyMatters: "A consistent, well-defined process is more important than recent results. You need to understand why the fund will behave a certain way before committing client money.",
      lookFor: ["Value vs. Growth vs. GARP style", "Top-Down vs. Bottom-Up analysis", "Active vs. Passive management", "Concentration policy — how many stocks?"],
      redFlag: "Style drift — a Value fund that starts buying expensive Growth stocks. The fund is no longer doing what it's supposed to.",
    },
    {
      letter: "P", name: "Performance", icon: "📈", color: green,
      definition: "How has the fund performed vs. its benchmark and peers — on a risk-adjusted basis?",
      whyMatters: "Short-term returns are often luck. Look at risk-adjusted returns over 3, 5, and 10 years. A fund that barely beats its benchmark with twice the volatility is not a good result.",
      lookFor: ["Risk-adjusted returns (Sharpe Ratio)", "Consistency — does it perform in all markets?", "3, 5, 10-year periods vs. benchmark", "Quartile ranking within peer group"],
      redFlag: "Last year's top performer. Chasing recent winners is the most common and costly investor mistake.",
    },
    {
      letter: "P", name: "Portfolio", icon: "📋", color: purple,
      definition: "What securities does the fund actually own right now?",
      whyMatters: "A fund's stated objective may not match its actual holdings. High concentration in a few stocks, unexpected sector bets, or overlap with other funds in the client's portfolio all matter.",
      lookFor: ["Concentration — top 10 holdings as % of assets", "Sector weightings vs. benchmark", "Geographic exposure", "Overlap with other funds in client portfolio"],
      redFlag: "Hidden concentration risk — a 'diversified' fund with 40% in one sector. Or two 'different' funds that hold the same 30 stocks.",
    },
    {
      letter: "P", name: "Price", icon: "💰", color: red,
      definition: "What does the fund cost in total — MER, trading costs, and fees?",
      whyMatters: "The MER is deducted before returns are shown. Lower fees are the single most reliable predictor of long-term relative performance. Every basis point matters, compounded over 20 years.",
      lookFor: ["Total MER (management + trailing + operating)", "Trading Expense Ratio (TER)", "Any deferred sales charges or redemption fees", "Whether fee reductions are available at higher investment amounts"],
      redFlag: "A fund charging 2.5% MER when an equivalent index ETF charges 0.2%. Over 20 years, the fee drag compounds into a very large dollar amount.",
    },
  ];
  const p = ps[active];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 0 }}>
        {ps.map((item, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ flex: 1, minWidth: 60, padding: "12px 8px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? item.color + "55" : border}`, borderBottom: active === i ? `2px solid ${item.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 700, color: active === i ? item.color : muted, transition: "all 0.2s", textAlign: "center" }}>
            <div style={{ fontSize: "1.1rem", marginBottom: 3 }}>{item.icon}</div>
            {item.name}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${p.color}22`, borderRadius: "0 0 8px 8px", padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 8, background: `${p.color}18`, border: `1px solid ${p.color}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", flexShrink: 0 }}>{p.icon}</div>
          <div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: white }}>{p.name}</div>
            <div style={{ fontSize: "0.78rem", color: p.color, fontStyle: "italic" }}>{p.definition}</div>
          </div>
        </div>
        <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          <div style={{ background: navy3, borderLeft: `3px solid ${blue2}`, borderRadius: 6, padding: "11px 16px" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: blue2, marginBottom: 6 }}>Why It Matters</div>
            <div style={{ fontSize: "0.84rem", color: textCol, lineHeight: 1.7 }}>{p.whyMatters}</div>
          </div>
          <div style={{ background: navy3, borderLeft: `3px solid ${green}`, borderRadius: 6, padding: "11px 16px" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: green, marginBottom: 6 }}>What to Look For</div>
            {p.lookFor.map((item, i) => <div key={i} style={{ fontSize: "0.83rem", color: textCol, marginBottom: 4 }}>→ {item}</div>)}
          </div>
          <div style={{ background: "rgba(239,68,68,.07)", borderLeft: `3px solid ${red}`, borderRadius: 6, padding: "11px 16px" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: red, marginBottom: 6 }}>⚠️ Red Flag</div>
            <div style={{ fontSize: "0.83rem", color: "#fca5a5", lineHeight: 1.7 }}>{p.redFlag}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── S3: Volatility & Risk ──
function VolatilityPanel() {
  const [sdValue, setSdValue] = useState(14);
  const [betaValue, setBetaValue] = useState(1.0);
  const [marketDrop, setMarketDrop] = useState(10);
  const [fundDrop, setFundDrop] = useState(8);

  const sdCategory = sdValue < 6 ? { label: "Low Volatility", color: green, examples: "Money Market, Short-term Bonds", rating: "Low" }
    : sdValue < 11 ? { label: "Low-to-Medium", color: blue2, examples: "Short-to-Medium Bond Funds", rating: "Low-to-Medium" }
    : sdValue < 16 ? { label: "Medium Volatility", color: gold, examples: "Balanced Funds, Large Cap Equity", rating: "Medium" }
    : sdValue < 20 ? { label: "Medium-to-High", color: "#f97316", examples: "Small Cap Equity, Global Equity", rating: "Medium-to-High" }
    : { label: "High Volatility", color: red, examples: "Emerging Markets, Precious Metals", rating: "High" };

  const betaLabel = betaValue > 1 ? { label: `${((betaValue - 1) * 100).toFixed(0)}% more volatile than market`, color: red, type: "Aggressive" }
    : betaValue < 1 ? { label: `${((1 - betaValue) * 100).toFixed(0)}% less volatile than market`, color: green, type: "Defensive" }
    : { label: "Moves exactly with the market", color: blue2, type: "Neutral" };

  const downside = fundDrop > 0 && marketDrop > 0 ? ((fundDrop / marketDrop) * 100).toFixed(0) : "—";
  const downsideGood = parseFloat(downside) < 100;

  return (
    <div style={{ margin: "28px 0", display: "grid", gap: 16 }}>
      {/* SD Explorer */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>📊 Standard Deviation → Risk Rating</div>
        <p style={{ fontSize: "0.82rem", color: muted, marginBottom: 18, lineHeight: 1.65 }}>Drag to see how SD maps to the Fund Facts risk rating used in industry.</p>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
          <label style={{ fontSize: "0.68rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>Std Dev</label>
          <input type="range" min={0} max={30} step={0.5} value={sdValue} onChange={e => setSdValue(parseFloat(e.target.value))} style={{ flex: 1, accentColor: sdCategory.color }} />
          <span style={{ fontFamily: "monospace", fontSize: "0.95rem", color: sdCategory.color, width: 50, textAlign: "right" }}>{sdValue}%</span>
        </div>
        <div style={{ background: navy4, border: `1px solid ${sdCategory.color}44`, borderRadius: 8, padding: "14px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: sdCategory.color }}>{sdCategory.label}</div>
            <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 10px", borderRadius: 100, color: sdCategory.color, background: `${sdCategory.color}15`, border: `1px solid ${sdCategory.color}33` }}>Fund Facts: {sdCategory.rating}</span>
          </div>
          <div style={{ fontSize: "0.8rem", color: muted, marginTop: 6 }}>Typical examples: {sdCategory.examples}</div>
        </div>
        {/* SD scale bar */}
        <div style={{ marginTop: 14 }}>
          <div style={{ height: 8, borderRadius: 4, background: `linear-gradient(90deg,${green},${blue2},${gold},#f97316,${red})`, marginBottom: 6 }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.62rem", color: muted }}>
            <span>0%</span><span>6%</span><span>11%</span><span>16%</span><span>20%</span><span>30%+</span>
          </div>
        </div>
      </div>

      {/* Beta Explorer */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>📐 Beta → Market Sensitivity</div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
          <label style={{ fontSize: "0.68rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>Beta</label>
          <input type="range" min={0.2} max={2.0} step={0.05} value={betaValue} onChange={e => setBetaValue(parseFloat(e.target.value))} style={{ flex: 1, accentColor: betaLabel.color }} />
          <span style={{ fontFamily: "monospace", fontSize: "0.95rem", color: betaLabel.color, width: 42, textAlign: "right" }}>{betaValue.toFixed(2)}</span>
        </div>
        <div style={{ background: navy4, border: `1px solid ${betaLabel.color}44`, borderRadius: 8, padding: "14px 18px", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontFamily: "monospace", fontSize: "1.4rem", fontWeight: 700, color: betaLabel.color }}>β = {betaValue.toFixed(2)}</div>
            <div style={{ fontSize: "0.75rem", color: betaLabel.color, marginTop: 2 }}>{betaLabel.type}</div>
          </div>
          <div style={{ fontSize: "0.84rem", color: textCol, lineHeight: 1.7 }}>{betaLabel.label}</div>
        </div>
      </div>

      {/* Downside Capture */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>📉 Downside Capture Ratio</div>
        <p style={{ fontSize: "0.82rem", color: muted, marginBottom: 16, lineHeight: 1.65 }}>How much does the fund fall when the market falls? Lower is better for risk-averse investors.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          {[["Market Drop (%)", marketDrop, setMarketDrop], ["Fund Drop (%)", fundDrop, setFundDrop]].map(([lbl, val, set], i) => (
            <div key={i}>
              <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
              <input type="number" step="0.1" value={val} onChange={e => set(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
            </div>
          ))}
        </div>
        <div style={{ background: navy4, border: `1px solid ${downsideGood ? green : red}33`, borderRadius: 8, padding: "14px 18px" }}>
          <div style={{ fontFamily: "monospace", fontSize: "1.8rem", fontWeight: 900, color: downsideGood ? green : red }}>{downside}%</div>
          <div style={{ fontSize: "0.82rem", color: textCol, marginTop: 6 }}>
            {downsideGood ? `✓ Fund dropped ${downside}% as much as the market — offers partial downside protection.`
              : `✗ Fund dropped ${downside}% as much as the market — amplifies losses. Bad for risk-averse clients.`}
          </div>
          <div style={{ fontFamily: "monospace", fontSize: "0.79rem", color: muted, marginTop: 8 }}>
            → {fundDrop}% ÷ {marketDrop}% × 100 = {downside}% downside capture
          </div>
        </div>
      </div>
    </div>
  );
}

// ── S4: Investment Styles ──
function InvestmentStylesPanel() {
  const [equityActive, setEquityActive] = useState(0);
  const [fiActive, setFiActive] = useState(0);

  const equityStyles = [
    {
      name: "Value", icon: "🏷️", color: blue2,
      tagline: "Bargain hunters — buy cheap, sell fair.",
      description: "Seeks stocks trading below their intrinsic value — low P/E ratios, ignored by the market. Relies on fundamental analysis to find 'hidden gems.'",
      peRatio: "Low (< 15×)", bestIn: "Recovering economies, market downturns, rising rates",
      risk: "Stocks may be cheap for a reason — 'value trap' risk if the business is structurally declining.",
      examples: ["Banks trading below book value", "Industrial companies in cyclical downturns", "Turnaround stories"],
    },
    {
      name: "Growth", icon: "🚀", color: gold,
      tagline: "Momentum investors — pay for future earnings.",
      description: "Buys companies with high earnings growth potential, even if expensive today. Accepts high P/E ratios in exchange for superior earnings growth.",
      peRatio: "High (30–100×+)", bestIn: "Expanding economies, bull markets, falling rates",
      risk: "Extremely sensitive to rate increases and earnings disappointments. High-flying stocks can crash quickly.",
      examples: ["Tech companies with rapid revenue growth", "Biotech with pipeline potential", "Disruptive platforms"],
    },
    {
      name: "GARP", icon: "⚖️", color: green,
      tagline: "Hybrid — growth, but not at any price.",
      description: "Growth At a Reasonable Price. Seeks companies with above-average earnings growth but avoids the extreme valuations of pure Growth investing.",
      peRatio: "Moderate (15–30×)", bestIn: "Most market conditions — more balanced approach",
      risk: "Harder to define than pure Value or Growth — manager judgment plays a large role.",
      examples: ["Quality consumer brands with steady 15% EPS growth", "Healthcare companies with strong pipelines at fair prices"],
    },
  ];

  const fiStyles = [
    {
      name: "Interest Rate Anticipation", icon: "📡", color: gold,
      description: "Manager actively adjusts the portfolio's duration based on interest rate forecasts. If rates are expected to fall → buy long-term bonds (increase duration). If rates expected to rise → buy short-term bonds (decrease duration).",
      risk: "Requires accurate rate forecasting — consistently difficult. Wrong calls can hurt the portfolio significantly.",
    },
    {
      name: "Credit Analysis", icon: "🔎", color: purple,
      description: "Identifies corporate bonds likely to receive a credit rating upgrade. An upgrade increases the bond's price. The manager researches company fundamentals to find undervalued credit before the market recognizes it.",
      risk: "Corporate analysis is complex. A missed deteriorating credit can result in sharp losses if a downgrade occurs.",
    },
  ];

  const es = equityStyles[equityActive];
  const fs = fiStyles[fiActive];

  return (
    <div style={{ margin: "28px 0" }}>
      {/* Equity Styles */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: muted, marginBottom: 10 }}>Equity Investment Styles</div>
        <div style={{ display: "flex", gap: 2 }}>
          {equityStyles.map((s, i) => (
            <button key={i} onClick={() => setEquityActive(i)} style={{ flex: 1, padding: "10px 8px", background: equityActive === i ? navy4 : navy3, border: `1px solid ${equityActive === i ? s.color + "55" : border}`, borderBottom: equityActive === i ? `2px solid ${s.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: equityActive === i ? s.color : muted, transition: "all 0.2s" }}>
              {s.icon} {s.name}
            </button>
          ))}
        </div>
        <div style={{ background: navy4, border: `1px solid ${es.color}22`, borderRadius: "0 0 8px 8px", padding: 22 }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 4 }}>{es.icon} {es.name} Style</div>
          <div style={{ fontSize: "0.78rem", color: es.color, fontStyle: "italic", marginBottom: 12 }}>{es.tagline}</div>
          <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.75, marginBottom: 14 }}>{es.description}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            {[["Typical P/E", es.peRatio, blue2], ["Best Market", es.bestIn, green]].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "10px 12px" }}>
                <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontSize: "0.82rem", color: textCol }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{ background: "rgba(239,68,68,.07)", border: `1px solid rgba(239,68,68,.2)`, borderRadius: 6, padding: "9px 14px", fontSize: "0.8rem", color: "#fca5a5", marginBottom: 10 }}>⚠️ {es.risk}</div>
          <div style={{ fontSize: "0.72rem", color: muted }}>Examples: {es.examples.join(" · ")}</div>
        </div>
      </div>

      {/* Fixed Income Styles */}
      <div>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: muted, marginBottom: 10 }}>Fixed-Income Investment Styles</div>
        <div style={{ display: "flex", gap: 2, marginBottom: 0 }}>
          {fiStyles.map((s, i) => (
            <button key={i} onClick={() => setFiActive(i)} style={{ flex: 1, padding: "10px 8px", background: fiActive === i ? navy4 : navy3, border: `1px solid ${fiActive === i ? s.color + "55" : border}`, borderBottom: fiActive === i ? `2px solid ${s.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 700, color: fiActive === i ? s.color : muted, transition: "all 0.2s" }}>
              {s.icon} {s.name}
            </button>
          ))}
        </div>
        <div style={{ background: navy4, border: `1px solid ${fs.color}22`, borderRadius: "0 0 8px 8px", padding: 22 }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 10 }}>{fs.icon} {fs.name}</div>
          <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.75, marginBottom: 12 }}>{fs.description}</div>
          <div style={{ background: "rgba(239,68,68,.07)", border: `1px solid rgba(239,68,68,.2)`, borderRadius: 6, padding: "9px 14px", fontSize: "0.8rem", color: "#fca5a5" }}>⚠️ {fs.risk}</div>
        </div>
      </div>
    </div>
  );
}

// ── S5: Tax Considerations ──
function TaxPanel() {
  const [acctType, setAcctType] = useState("nonreg");

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {[{ key: "reg", label: "🏦 Registered (RRSP/TFSA)", color: green }, { key: "nonreg", label: "📊 Non-Registered", color: gold }].map(btn => (
          <button key={btn.key} onClick={() => setAcctType(btn.key)} style={{ flex: 1, padding: "12px 14px", background: acctType === btn.key ? navy4 : navy3, border: `1px solid ${acctType === btn.key ? btn.color + "55" : border}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.8rem", fontWeight: 700, color: acctType === btn.key ? btn.color : muted, transition: "all 0.2s" }}>{btn.label}</button>
        ))}
      </div>

      {acctType === "reg" ? (
        <div style={{ background: navy3, border: `1px solid ${green}33`, borderRadius: 8, padding: 24 }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 12 }}>🏦 Registered Accounts (RRSP / TFSA)</div>
          <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.8, marginBottom: 16 }}>
            Inside registered accounts, <strong style={{ color: green }}>tax efficiency does not matter</strong> — there is no annual tax on income, dividends, or capital gains. Investments grow tax-sheltered. The only goal is maximizing <strong>total return</strong>.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {[["Focus On", "Total return — highest risk-adjusted return available", green], ["Bond Funds Here?", "Yes — interest is fully taxable outside, so shelter it inside a registered account", green], ["High Turnover OK?", "Yes — capital gains within a registered account are not taxable", green]].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: navy4, borderRadius: 6, padding: "10px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, width: 120, flexShrink: 0 }}>{lbl}</span>
                <span style={{ fontSize: "0.84rem", color: textCol, flex: 1 }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ background: navy3, border: `1px solid ${gold}33`, borderRadius: 8, padding: 24 }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 12 }}>📊 Non-Registered Accounts</div>
          <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.8, marginBottom: 16 }}>
            Outside registered accounts, tax efficiency becomes <strong style={{ color: gold }}>critically important</strong>. Different types of income are taxed at different rates — and the fund's portfolio management can trigger unexpected tax bills.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {[
              { type: "Capital Gains", rate: "50% taxable", verdict: "PREFERRED ✓", color: green, note: "Only half the gain is included in income. Best tax treatment." },
              { type: "Canadian Dividends", rate: "Gross-up + DTC", verdict: "PREFERRED ✓", color: green, note: "Dividend Tax Credit reduces effective rate. Better than interest." },
              { type: "Interest Income", rate: "100% taxable", verdict: "AVOID ✗", color: red, note: "Fully included in income at your marginal rate. Worst tax treatment." },
              { type: "High Turnover Funds", rate: "Triggers capital gains", verdict: "AVOID ✗", color: red, note: "Each sale inside the fund may generate a capital gain distribution to unitholders — taxable even if distributions are reinvested." },
            ].map((item, i) => (
              <div key={i} style={{ background: navy4, borderLeft: `3px solid ${item.color}`, borderRadius: 6, padding: "11px 16px", display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div style={{ minWidth: 160 }}>
                  <div style={{ fontSize: "0.82rem", fontWeight: 700, color: white }}>{item.type}</div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: item.color, marginTop: 2 }}>{item.rate}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 100, color: item.color, background: `${item.color}18`, border: `1px solid ${item.color}33`, marginBottom: 4, display: "inline-block" }}>{item.verdict}</span>
                  <div style={{ fontSize: "0.78rem", color: muted, marginTop: 4, lineHeight: 1.6 }}>{item.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Calculators ──
function MERImpactCalc() {
  const [gross, setGross] = useState(8);
  const [merA, setMerA] = useState(2.5);
  const [merB, setMerB] = useState(1.0);
  const [invest, setInvest] = useState(10000);
  const [years, setYears] = useState(20);
  const [result, setResult] = useState(null);

  function calc() {
    const g = parseFloat(gross), a = parseFloat(merA), b = parseFloat(merB);
    const inv = parseFloat(invest), yr = parseFloat(years);
    const netA = g - a, netB = g - b;
    const fvA = inv * Math.pow(1 + netA / 100, yr);
    const fvB = inv * Math.pow(1 + netB / 100, yr);
    const diff = fvB - fvA;
    setResult({ netA: netA.toFixed(1), netB: netB.toFixed(1), fvA: fvA.toFixed(0), fvB: fvB.toFixed(0), diff: diff.toFixed(0) });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "16px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 MER Impact Calculator — The Fee Drag Over Time</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 18, display: "inline-block", lineHeight: 1.8 }}>
        Net Return = Gross Return − MER<br />
        Future Value = Investment × (1 + Net Return)^Years
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 14 }}>
        {[["Gross Return (%)", gross, setGross], ["Fund A MER (%)", merA, setMerA], ["Fund B MER (%)", merB, setMerB], ["Investment ($)", invest, setInvest], ["Years", years, setYears]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="any" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.85rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "11px 24px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            {[{ label: `Fund A (${merA}% MER)`, net: result.netA, fv: result.fvA, color: red }, { label: `Fund B (${merB}% MER)`, net: result.netB, fv: result.fvB, color: green }].map((card, i) => (
              <div key={i} style={{ background: navy4, border: `1px solid ${card.color}33`, borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: card.color, marginBottom: 6 }}>{card.label}</div>
                <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted, marginBottom: 4 }}>Net: {card.net}%/yr</div>
                <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", fontWeight: 700, color: white }}>${parseInt(card.fv).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div style={{ background: "rgba(245,166,35,.08)", border: "1px solid rgba(245,166,35,.25)", borderRadius: 8, padding: "16px 18px" }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1.4rem", color: gold, marginBottom: 4 }}>${parseInt(result.diff).toLocaleString()} lost to higher fees</div>
            <div style={{ fontSize: "0.82rem", color: textCol, lineHeight: 1.7 }}>Over {years} years, the {parseFloat(merA) - parseFloat(merB)}% MER difference compounds into a massive wealth gap. Every basis point in fees is a permanent, compounding drag on returns.</div>
            {[
              `Fund A net: ${gross}% − ${merA}% = ${result.netA}%  →  $${parseInt(result.fvA).toLocaleString()} after ${years} yrs`,
              `Fund B net: ${gross}% − ${merB}% = ${result.netB}%  →  $${parseInt(result.fvB).toLocaleString()} after ${years} yrs`,
              `Difference: $${parseInt(result.fvB).toLocaleString()} − $${parseInt(result.fvA).toLocaleString()} = $${parseInt(result.diff).toLocaleString()} — lost purely to fees`,
            ].map((step, i) => (
              <div key={i} style={{ fontFamily: "monospace", fontSize: "0.78rem", color: i === 2 ? gold : muted, marginTop: 6, display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BetaCAPMCalc() {
  const [market, setMarket] = useState(10);
  const [riskFree, setRiskFree] = useState(2);
  const [beta, setBeta] = useState(1.2);
  const [result, setResult] = useState(null);

  function calc() {
    const m = parseFloat(market), r = parseFloat(riskFree), b = parseFloat(beta);
    const premium = m - r;
    const riskAdj = b * premium;
    const expected = r + riskAdj;
    setResult({ m, r, b, premium: premium.toFixed(1), riskAdj: riskAdj.toFixed(1), expected: expected.toFixed(1) });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "16px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 Beta / CAPM Expected Return</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 18, display: "inline-block", lineHeight: 1.8 }}>
        Expected Return = Risk-Free Rate + Beta × (Market Return − Risk-Free Rate)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 14 }}>
        {[["Market Return (%)", market, setMarket], ["Risk-Free Rate (%)", riskFree, setRiskFree], ["Fund Beta", beta, setBeta]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="0.1" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "11px 24px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 18, marginTop: 14 }}>
          <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 4 }}>Expected Return (CAPM)</div>
          <div style={{ fontFamily: "monospace", fontSize: "2rem", fontWeight: 900, color: white, marginBottom: 12 }}>{result.expected}%</div>
          {[
            `Market risk premium: ${result.m}% − ${result.r}% = ${result.premium}%`,
            `Risk adjustment: β${result.b} × ${result.premium}% = ${result.riskAdj}%`,
            `Expected return: ${result.r}% + ${result.riskAdj}% = ${result.expected}%`,
          ].map((step, i) => (
            <div key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "3px 0", display: "flex", gap: 8 }}>
              <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "When analyzing a mutual fund, 'Tenure' refers to:", opts: ["The time until the fund matures", "The length of time the current portfolio manager has managed the fund", "The time horizon of the client", "The duration of the bond portfolio"], correct: 1, fb: { y: "Correct! Manager tenure measures how long the current portfolio manager has been running the fund. A long track record with the same manager is meaningful — a short one means past performance belongs to someone who left.", n: "Not quite. Tenure = how long the current manager has been in their role. This is critical because a fund's track record belongs to its manager, not the fund name." } },
  { q: "A 'Value' manager typically looks for stocks with:", opts: ["High P/E ratios and high growth rates", "Low P/E ratios and prices below intrinsic value", "Strong price momentum", "No dividend history"], correct: 1, fb: { y: "Correct! Value managers are bargain hunters — they buy stocks the market has overlooked or discounted, with low P/E ratios and prices below estimated intrinsic value.", n: "Not quite. Value = cheap stocks with low P/E ratios. Growth = expensive stocks with high P/E ratios and earnings momentum. They are opposite investment philosophies." } },
  { q: "If a client has a 'Low' risk tolerance, which Standard Deviation range is most appropriate?", opts: ["Under 6%", "10% to 15%", "Over 20%", "Negative Standard Deviation"], correct: 0, fb: { y: "Correct! Standard deviation under 6% corresponds to low-volatility funds (Money Market, Short-term Bond). This matches a low risk tolerance investor on the Fund Facts risk scale.", n: "Not quite. Under 6% SD = Low risk (money market, short-term bonds). 10–15% = Medium (balanced, large cap equity). Over 20% = High (emerging markets, precious metals)." } },
  { q: "Which factor is the most reliable predictor of future relative performance?", opts: ["Last year's return", "The fund's star rating", "Management Expense Ratio (fees)", "The size of the fund"], correct: 2, fb: { y: "Correct! Academic research consistently shows that lower fees are the single most reliable predictor of better long-term relative performance. High fees create a permanent, compounding drag on returns.", n: "Not quite. Past returns, star ratings, and fund size have poor predictive power. The MER (fees) is statistically the most reliable predictor — lower fees = better net long-term performance." } },
  { q: "A 'Top-Down' analysis starts with:", opts: ["Picking individual stocks first", "Analyzing the global/macroeconomic environment", "Looking at the fund's fees first", "Interviewing the company's CEO"], correct: 1, fb: { y: "Correct! Top-Down analysis flows from macro to micro: Economy → Industry/Sector → Individual Companies. You first understand the economic backdrop before selecting specific securities.", n: "Not quite. Top-Down starts at the top — the macroeconomic environment — then narrows to sectors and finally individual stocks. Bottom-Up starts with individual company analysis regardless of macro." } },
  { q: "Why should an investor avoid high-turnover funds in a non-registered account?", opts: ["They have lower expected returns", "They trigger frequent capital gains distributions, increasing the tax bill", "They are prohibited in non-registered accounts", "They only invest in bonds"], correct: 1, fb: { y: "Correct! Every time a fund manager sells a security at a profit, it generates a capital gain that must be distributed to unitholders — who then pay tax on it, even if they reinvested. High turnover = frequent taxable events.", n: "Not quite. High-turnover funds trigger capital gains distributions frequently — taxable in non-registered accounts. This creates an unpredictable tax bill even if you didn't sell your units." } },
  { q: "If a fund has a Downside Capture Ratio of 110%, what does this mean?", opts: ["It protects capital well in market downturns", "It falls 10% more than the market when the market drops", "It captures 110% of the market's upside", "It has 110 basis points in fees"], correct: 1, fb: { y: "Correct! A downside capture of 110% means the fund drops 10% MORE than the market during downturns — it amplifies losses. This is bad for risk-averse investors. Lower downside capture is always better.", n: "Not quite. 110% downside capture = the fund is 10% WORSE than the market in downturns. If the market drops 10%, this fund drops 11%. Lower is always better for downside protection." } },
  { q: "Comparing a Canadian Equity Fund to the S&P 500 Index is an example of:", opts: ["Correct benchmarking", "Mismatched benchmarking", "Peer group analysis", "Top-Down analysis"], correct: 1, fb: { y: "Correct! The S&P 500 is a U.S. equity benchmark. A Canadian equity fund should be compared to the S&P/TSX Composite Index. Mismatched benchmarks make performance evaluation meaningless.", n: "Not quite. This is mismatched benchmarking. The S&P 500 represents U.S. stocks. Canadian equity funds must be compared to the S&P/TSX Composite — otherwise the comparison is invalid." } },
  { q: "Which document provides the mandatory risk rating for a mutual fund?", opts: ["The Prospectus", "The Fund Facts", "The Annual Report", "The KYC Form"], correct: 1, fb: { y: "Correct! The Fund Facts document is the standardized, plain-language disclosure document that includes the mandatory risk rating (based on standard deviation) alongside the fund's costs, top holdings, and past performance.", n: "Not quite. The Fund Facts contains the mandatory, standardized risk rating. It's a concise 2-page document required by regulation — designed for investors, not the detailed Prospectus." } },
  { q: "'Style Drift' occurs when:", opts: ["A Value manager starts buying expensive Growth stocks", "A manager retires from the fund", "The stock market crashes sharply", "The fund's MER increases"], correct: 0, fb: { y: "Correct! Style drift means the fund is no longer following its stated investment process. A 'Value' fund buying expensive Growth stocks is an example — investors chose the fund for a reason, and drift breaks that promise.", n: "Not quite. Style drift = a manager deviates from the fund's stated investment approach. It matters because investors chose the fund expecting a specific style — drift invalidates that choice." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);
  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 15 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered the fund selection process." : score >= 8 ? "Excellent — review any missed questions." : score >= 6 ? "Good start. Revisit the 5 P's, investment styles, and tax considerations." : "Focus on the 5 P's, MER importance, investment styles, and downside capture."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Top-Down Analysis", def: "Selection approach starting with macroeconomic analysis → sector selection → specific fund/security. Broad to narrow." },
  { term: "KYC (Know Your Client)", def: "The client profile that must be established before any recommendation — time horizon, risk tolerance, risk capacity, and return objective." },
  { term: "Risk Tolerance", def: "The client's willingness to accept losses — psychological comfort with volatility." },
  { term: "Risk Capacity", def: "The client's financial ability to absorb losses — determined by income, assets, time horizon, and obligations." },
  { term: "Asset Allocation", def: "The decision of how to split a portfolio across asset classes (equity/fixed income/cash). Accounts for 90%+ of return variability." },
  { term: "The 5 P's", def: "Framework for evaluating a fund: People (manager tenure), Process (style), Performance (risk-adjusted), Portfolio (holdings), Price (MER)." },
  { term: "Manager Tenure", def: "How long the current portfolio manager has been running the fund. Key People factor — past returns mean little if the manager changed." },
  { term: "Style Drift", def: "When a manager deviates from the fund's stated investment philosophy (e.g., a Value manager buying expensive Growth stocks)." },
  { term: "Value Investing", def: "Buying stocks trading below their intrinsic value — low P/E ratios. Best in recovering economies and downturns." },
  { term: "Growth Investing", def: "Buying companies with high earnings growth potential — high P/E ratios. Best in expanding economies and bull markets." },
  { term: "GARP", def: "Growth At a Reasonable Price — a hybrid seeking growth stocks but avoiding extreme valuations. Moderate P/E ratios." },
  { term: "Downside Capture Ratio", def: "How much of the market's loss the fund absorbs. Fund drop ÷ Market drop × 100. Lower is better — means more downside protection." },
  { term: "Standard Deviation (Fund Facts)", def: "Used to assign the Fund Facts risk rating: Under 6% = Low; 11–16% = Medium; Over 20% = High." },
  { term: "MER (as Predictor)", def: "Lower MER is the single most reliable predictor of better long-term relative fund performance. Fees are a permanent drag." },
  { term: "Interest Rate Anticipation", def: "Fixed-income strategy of adjusting duration based on rate forecasts. Rates falling → increase duration; rates rising → decrease." },
  { term: "Credit Analysis Style", def: "Fixed-income strategy seeking corporate bonds likely to receive a credit rating upgrade, increasing their price." },
  { term: "High-Turnover Fund", def: "A fund that frequently buys and sells securities. In non-registered accounts, each sale may trigger taxable capital gain distributions." },
  { term: "Registered Account (Tax)", def: "RRSP/TFSA — no immediate tax on income, dividends, or capital gains. Tax efficiency irrelevant; focus on total return." },
  { term: "Non-Registered Account (Tax)", def: "Tax efficiency critical. Prefer capital gains (50% taxable) and Canadian dividends (DTC). Avoid interest income (100% taxable)." },
  { term: "CAPM", def: "Capital Asset Pricing Model: Expected Return = Risk-Free Rate + Beta × (Market Return − Risk-Free Rate). Prices risk via beta." },
];

// ── Main Export ──
export default function Chapter15() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "The Top-Down Selection Approach"],
    ["02", "The 5 P's — People, Process, Performance, Portfolio, Price"],
    ["03", "Volatility & Risk — SD, Beta, Downside Capture"],
    ["04", "Investment Styles — Value, Growth, GARP, Fixed Income"],
    ["05", "Tax Considerations — Registered vs. Non-Registered"],
    ["🧮", "MER Impact & Beta / CAPM Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 15
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Mutual Fund <span style={{ color: gold }}>Selection Process</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              The structured top-down process for matching a fund to a client's KYC profile — the 5 P's framework, volatility measures, investment styles, tax considerations, and the devastating long-term cost of high fees.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","5","Core Topics"],["⏱","~20 min","read"],["🎯","10","practice questions"],["🧮","2","calculators"],["📊","3","interactive tools"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* S1 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 01</span><span style={ss.badge}>Top-Down Process</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>The Top-Down Selection Approach</h2>
            <p style={ss.prose}>
              Selecting a mutual fund is not about chasing last year's winner. It's a <span style={{ color: gold, fontWeight: 600 }}>structured three-step process</span> that starts with the big picture and narrows to a specific fund — always anchored to the client's KYC profile:
            </p>
          </Reveal>
          <Reveal delay={0.1}><TopDownFlow /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="🎯">
              <strong>KYC First, Always.</strong> Before looking at any fund, establish the client's Time Horizon, Risk Tolerance (willingness), Risk Capacity (financial ability), and Return Objective (income vs. growth vs. safety). Asset allocation — the mix between equities and fixed income — accounts for over <strong>90% of long-term return variability</strong>. Get that right first.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S2 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 02</span><span style={ss.badge}>The 5 P's</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>The 5 P's Framework</h2>
            <p style={ss.prose}>
              When comparing two funds in the same category (e.g., two Canadian equity funds), evaluate them on five dimensions. Click each P to see why it matters, what to look for, and the red flag that disqualifies a fund:
            </p>
          </Reveal>
          <Reveal delay={0.1}><FivePsExplorer /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="💰" accent={red}>
              <strong style={{ color: red }}>Price Is the Most Reliable Predictor.</strong> Academic research consistently shows that after controlling for other factors, <strong>lower MER funds outperform higher MER funds</strong> in the long run. A fund charging 2.5% MER has to outperform a 0.5% ETF by 2% every single year just to break even — before compounding.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S3 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 03</span><span style={ss.badge}>Volatility & Risk</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Analyzing Volatility &amp; Risk</h2>
            <p style={ss.prose}>
              Three tools for quantifying a fund's risk — Standard Deviation (total volatility), Beta (market sensitivity), and Downside Capture (how badly it falls in crashes). Use the interactive tools below:
            </p>
          </Reveal>
          <Reveal delay={0.1}><VolatilityPanel /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S4 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 04</span><span style={ss.badge}>Investment Styles</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Investment Styles</h2>
            <p style={ss.prose}>
              A fund's investment style determines how it behaves in different market conditions — and whether it does what the client expects. Style consistency matters as much as past performance:
            </p>
          </Reveal>
          <Reveal delay={0.1}><InvestmentStylesPanel /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="⚠️" accent={purple}>
              <strong style={{ color: purple }}>Style Drift Warning:</strong> If a Value manager starts buying expensive Growth stocks, the fund is no longer behaving as advertised. Investors who chose it for Value exposure now have unintended Growth risk. Style drift violates the implicit promise made to clients — always check for it.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S5 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 05</span><span style={ss.badge}>Tax Considerations</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Tax Considerations</h2>
            <p style={ss.prose}>
              Where a fund is held matters as much as which fund is chosen. Tax efficiency is <span style={{ color: gold, fontWeight: 600 }}>irrelevant inside registered accounts</span> but becomes <span style={{ color: red, fontWeight: 600 }}>critically important in non-registered accounts</span>:
            </p>
          </Reveal>
          <Reveal delay={0.1}><TaxPanel /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Calculators */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Calculators</span><span style={ss.badge}>MER Impact & CAPM</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>MER Impact &amp; Beta / CAPM</h2>
            <p style={ss.prose}>Two essential Chapter 15 calculations — the compounding cost of high fees over time, and the CAPM formula for expected return given a fund's beta:</p>
          </Reveal>
          <Reveal delay={0.1}><MERImpactCalc /></Reveal>
          <Reveal delay={0.2}><BetaCAPMCalc /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Quiz */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Knowledge Check</span><span style={ss.badge}>10 Questions</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Test Your Understanding</h2>
            <p style={ss.prose}>10 questions covering the 5 P's, investment styles, volatility measures, tax efficiency, and the fund selection process from Chapter 15.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Glossary */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Quick Reference</span><span style={ss.badge}>Key Terms</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Glossary</h2>
            <p style={ss.prose}>All essential terms from Chapter 15 — fund selection framework, investment styles, risk measures, and tax considerations.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
